import React, { FC, forwardRef, ReactNode } from 'react';
import { CalendarContainer, default as ReactDatePicker } from 'react-datepicker';
import { format } from 'date-fns';
import {
  Black,
  Blue80,
  ButtonIcon,
  Gold80,
  Gray10,
  Gray100,
  Gray50,
  TextInput,
  typeCompact12Medium,
  typeCompact14Medium,
} from '@traceair/tds';
import { classes, style, stylesheet } from 'typestyle';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from '@traceair/webapp-icons';

type ContainerProps = {
  className?: string;
  children: ReactNode;
};

type HeaderProps = {
  date: number | Date;
  decreaseMonth: () => void;
  increaseMonth: () => void;
};

type DatePickerProps = {
  date?: Date;
  placeholder?: string;
  onChange?: (date?: Date) => void;
  maxDate?: Date;
  minDate?: Date;
  isWarning?: boolean;
  warningMessage?: string;
  isDisabled?: boolean;
  ourOnClick?: () => void;
  isInline?: boolean;
  highlightDates?: Date[];
  highlightedDatesGrayLight?: Date[];
};

const FlightDatePicker: FC<DatePickerProps> = ({
  date,
  placeholder,
  onChange,
  maxDate,
  minDate,
  isDisabled,
  ourOnClick,
  isInline,
  highlightDates = [],
  highlightedDatesGrayLight = [],
  isWarning,
  warningMessage,
}) => {
  const highlighted: Array<Record<string, Date[]>> = [
    { 'react-datepicker__day--highlighted': highlightDates },
    { 'react-datepicker__day--highlighted-sky': highlightedDatesGrayLight },
  ];

  function handleChange(date?: Date | null) {
    if (onChange) onChange(date || undefined);
  }

  const DateContainer = forwardRef<HTMLInputElement, { onClick?: () => void }>(({ onClick }, ref) => (
    <>
      <div
        className={classes(pickerRootStyles.common, isDisabled && pickerRootStyles.disabled)}
        onClick={
          isDisabled
            ? undefined
            : () => {
                ourOnClick && ourOnClick();
                onClick && onClick();
              }
        }
        ref={ref}
      >
        <TextInput
          disabled={isDisabled}
          placeholder={placeholder}
          label="Date"
          value={date && format(date, 'd LLL yyyy')}
          rightCell={
            !isDisabled && (
              <div style={{ pointerEvents: 'none' }}>
                <CalendarIcon color={Gray100} />
              </div>
            )
          }
          onChange={() => undefined}
        />
      </div>
      {!isDisabled && isWarning && warningMessage && (
        <div className={classes(warningMessageStyle, typeCompact12Medium)}>{warningMessage}</div>
      )}
    </>
  ));

  DateContainer.displayName = 'DateContainer';

  const MyContainer: FC<ContainerProps> = ({ className, children }) => {
    return (
      <CalendarContainer className={className} showPopperArrow={false}>
        {children}
      </CalendarContainer>
    );
  };

  const Header: FC<HeaderProps> = ({ date, decreaseMonth, increaseMonth }) => (
    <div className={headerStyle}>
      <div className={typeCompact14Medium} style={{ color: Black }}>
        {format(date, 'MMMM, yyyy')}
      </div>
      <div style={{ display: 'flex' }}>
        <ButtonIcon onClick={decreaseMonth} style="secondary" icon={<ChevronLeftIcon color={Blue80} />} size="xs" />
        <div style={{ marginLeft: 4 }}>
          <ButtonIcon onClick={increaseMonth} style="secondary" icon={<ChevronRightIcon color={Blue80} />} size="xs" />
        </div>
      </div>
    </div>
  );

  return (
    <ReactDatePicker
      selected={date}
      onChange={handleChange}
      customInput={<DateContainer />}
      calendarContainer={MyContainer}
      renderCustomHeader={Header}
      maxDate={maxDate}
      minDate={minDate}
      useWeekdaysShort={true}
      popperPlacement="bottom"
      popperModifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, -6],
          },
        },
        { name: 'flip', enabled: false },
      ]}
      inline={isInline}
      highlightDates={highlighted}
      disabledKeyboardNavigation
    />
  );
};

const pickerRootStyles = stylesheet({
  common: {
    height: 40,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: 12,
    cursor: 'pointer',
  },
  disabled: {
    borderColor: Gray50,
    backgroundColor: Gray10,
  },
});

const headerStyle = style({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 12px',
});

const warningMessageStyle = style({
  marginTop: 2,
  textAlign: 'left',
  color: Gold80,
});

export default FlightDatePicker;
